import { useEditorState } from "@tiptap/react";
import clsx from "clsx";
import React from "react";
import Button from "../../common/Button/Button";
import FloatingPanel from "../../common/FloatingPanel/FloatingPanel";
import Icon from "../../common/Icon/Icon";
import Popover from "../../common/Popover/Popover";
import styles from "./RichTextEditor.module.scss";
import { preserveSelection, useRichTextEditor } from "./context";
import { type BadgeVariant, TEXT_COLORS, TEXT_COLOR_NAMES, type TextColorName } from "./textColors";

interface Swatch {
  label: string;
  selected: boolean;
  onPick: () => void;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

const SwatchButton = ({ label, selected, onPick, className, style, children }: Swatch) => (
  <button
    type="button"
    className={clsx(styles.swatch, className)}
    style={style}
    aria-label={label}
    title={label}
    aria-pressed={selected}
    data-selected={selected || undefined}
    onMouseDown={preserveSelection}
    onClick={onPick}
  >
    {children}
  </button>
);

const NoneSwatch = ({ label, selected, onPick }: Omit<Swatch, "children">) => (
  <SwatchButton
    label={label}
    selected={selected}
    onPick={onPick}
    className={styles.swatchNone}
  >
    <Icon
      name="ban"
      aria-hidden
    />
  </SwatchButton>
);

/**
 * Text colour control: a popover of text colours, highlights and badges, with
 * "Remove color" to clear all three. Needs the `TextColors` extension. The
 * trigger's "A" shows the colour at the cursor.
 */
export const RichTextEditorColor = () => {
  const { editor, disabled } = useRichTextEditor();
  const [open, setOpen] = React.useState(false);
  const current = useEditorState({
    editor,
    selector: ({ editor: instance }) => {
      const attrs = instance?.getAttributes("textStyle") ?? {};
      return {
        color: (attrs.color as string | null) ?? null,
        highlight: (attrs.highlight as string | null) ?? null,
        badge: (attrs.badge as string | null) ?? null,
        badgeVariant: (attrs.badgeVariant as string | null) === "subtle" ? "subtle" : "solid",
      };
    },
  });

  const run = (apply: () => void) => {
    apply();
    setOpen(false);
  };
  const setColor = (color: TextColorName | null) => run(() => editor?.chain().focus().setTextColor(color).run());
  const setHighlight = (color: TextColorName | null) =>
    run(() => editor?.chain().focus().setTextHighlight(color).run());
  const setBadge = (color: TextColorName | null, variant: BadgeVariant = "solid") =>
    run(() => editor?.chain().focus().setTextBadge(color, variant).run());

  const colorEntry = current?.color ? TEXT_COLORS[current.color as TextColorName] : undefined;
  const badgeEntry = current?.badge ? TEXT_COLORS[current.badge as TextColorName] : undefined;
  const glyphStyle: React.CSSProperties | undefined = badgeEntry
    ? current?.badgeVariant === "subtle"
      ? { backgroundColor: badgeEntry.tint, color: badgeEntry.text, borderColor: "transparent" }
      : { backgroundColor: badgeEntry.solid, color: badgeEntry.onSolid, borderColor: "transparent" }
    : colorEntry
      ? { color: colorEntry.text }
      : undefined;
  const highlightEntry = current?.highlight ? TEXT_COLORS[current.highlight as TextColorName] : undefined;

  return (
    <Popover
      visible={open}
      onVisibleChange={setOpen}
      mode="click"
      interactable
      placement="bottom-start"
      offset={6}
    >
      {(popover) => (
        <>
          <Popover.Reference
            asChild
            trigger={false}
          >
            <Button
              icon
              size="sm"
              label="Text color"
              selected={open}
              disabled={!editor || disabled}
              onMouseDown={preserveSelection}
              onClick={() => setOpen(!open)}
            >
              <span
                className={styles.colorGlyph}
                style={{
                  ...glyphStyle,
                  ...(highlightEntry && !badgeEntry ? { backgroundColor: highlightEntry.tint } : {}),
                }}
                aria-hidden
              >
                A
              </span>
            </Button>
          </Popover.Reference>
          <Popover.Content
            asChild
            handleVisibility={false}
          >
            <FloatingPanel
              open={popover.visible}
              placement={popover.placement}
              unmountWhenHidden
              className={styles.colorPanel}
            >
              <div className={styles.colorSection}>
                <div className={styles.colorHeading}>Text colors</div>
                <div className={styles.swatchRow}>
                  {TEXT_COLOR_NAMES.map((name) => (
                    <SwatchButton
                      key={name}
                      label={`${TEXT_COLORS[name].label} text`}
                      selected={current?.color === name}
                      onPick={() => setColor(name)}
                      className={styles.swatchText}
                      style={{ color: TEXT_COLORS[name].text }}
                    >
                      A
                    </SwatchButton>
                  ))}
                  <SwatchButton
                    label="Default text"
                    selected={!current?.color && !current?.badge}
                    onPick={() => setColor(null)}
                    className={styles.swatchText}
                  >
                    A
                  </SwatchButton>
                </div>
              </div>

              <div className={styles.colorSection}>
                <div className={styles.colorHeading}>Text highlights</div>
                <div className={styles.swatchRow}>
                  {TEXT_COLOR_NAMES.map((name) => (
                    <SwatchButton
                      key={name}
                      label={`${TEXT_COLORS[name].label} highlight`}
                      selected={current?.highlight === name}
                      onPick={() => setHighlight(name)}
                      className={styles.swatchRound}
                      style={{ backgroundColor: TEXT_COLORS[name].tint }}
                    />
                  ))}
                  <NoneSwatch
                    label="No highlight"
                    selected={false}
                    onPick={() => setHighlight(null)}
                  />
                </div>
              </div>

              <div className={styles.colorSection}>
                <div className={styles.colorHeading}>Badges</div>
                <div className={styles.swatchRow}>
                  {TEXT_COLOR_NAMES.map((name) => (
                    <SwatchButton
                      key={name}
                      label={`${TEXT_COLORS[name].label} badge`}
                      selected={current?.badge === name && current.badgeVariant === "solid"}
                      onPick={() => setBadge(name, "solid")}
                      style={{ backgroundColor: TEXT_COLORS[name].solid }}
                    />
                  ))}
                  <NoneSwatch
                    label="No badge"
                    selected={false}
                    onPick={() => setBadge(null)}
                  />
                </div>
                <div className={styles.swatchRow}>
                  {TEXT_COLOR_NAMES.map((name) => (
                    <SwatchButton
                      key={name}
                      label={`${TEXT_COLORS[name].label} subtle badge`}
                      selected={current?.badge === name && current.badgeVariant === "subtle"}
                      onPick={() => setBadge(name, "subtle")}
                      style={{ backgroundColor: TEXT_COLORS[name].tint }}
                    />
                  ))}
                </div>
              </div>

              <Button
                label="Remove color"
                variant="outline"
                size="sm"
                className={styles.removeColor}
                onMouseDown={preserveSelection}
                onClick={() => run(() => editor?.chain().focus().unsetTextColors().run())}
              >
                <Icon
                  name="ban"
                  aria-hidden
                />
                Remove color
              </Button>
            </FloatingPanel>
          </Popover.Content>
        </>
      )}
    </Popover>
  );
};
